package co.maytes.standupbot.dashboard.pages.app.templates

import co.maytes.standupbot.dashboard.styles.Layout
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.ConfirmModal
import co.maytes.standupbot.dashboard.components.feedback.ConfirmTone
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.feedback.SkeletonBox
import co.maytes.standupbot.dashboard.components.feedback.SkeletonCard
import co.maytes.standupbot.dashboard.components.feedback.SkeletonLine
import co.maytes.standupbot.dashboard.components.layout.AdminGuard
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.AppPageHeader
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.features.standups.rememberStandupsStore
import co.maytes.standupbot.dashboard.features.templates.TemplatePreviewPane
import co.maytes.standupbot.dashboard.features.templates.TestFireModal
import co.maytes.standupbot.dashboard.features.templates.rememberTemplatesStore
import co.maytes.standupbot.dashboard.features.templateslist.TemplatesListContainer
import co.maytes.standupbot.dashboard.features.templateslist.TemplatesListContract
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.template.TemplateDto
import co.maytes.standupbot.shared.util.RelativeTime
import com.kttipay.mvi.compose.subscribe
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideClock
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideFileText
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideList
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Clock
import org.jetbrains.compose.web.css.px

@Page("/app/templates")
@Composable
fun TemplatesIndexPage() = AuthGuard {
    AdminGuard {
        AppShell {
            TemplatesListScreen()
        }
    }
}

@Composable
private fun TemplatesListScreen() {
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val templatesStore = rememberTemplatesStore()
    val standupsStore = rememberStandupsStore()
    val scope = rememberCoroutineScope()
    var selectedId by remember { mutableStateOf<String?>(null) }
    var testingTemplate by remember { mutableStateOf<TemplateDto?>(null) }

    val container = remember(templatesStore) { TemplatesListContainer(scope, templatesStore) }

    val state by container.subscribe { action ->
        when (action) {
            is TemplatesListContract.Action.Navigate -> ctx.router.navigateTo(action.path)
            is TemplatesListContract.Action.Toast -> ui.showToast(
                action.message,
                if (action.error) ToastKind.Error else ToastKind.Success,
            )
        }
    }

    val workspaceId = auth.currentUser?.workspace?.id
    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) {
            container.intent(TemplatesListContract.Intent.Load(workspaceId))
        }
    }

    TemplatesListView(
        state = state,
        selectedId = selectedId,
        workspaceName = auth.currentUser?.workspace?.name,
        onToggleSelect = { id -> selectedId = if (selectedId == id) null else id },
        onTest = { tpl -> testingTemplate = tpl },
        dispatch = { container.intent(it) },
    )

    state.deleteTargetId?.let {
        ConfirmModal(
            title = "Delete template?",
            body = "Standups using this template will be left without one. This cannot be undone.",
            confirmLabel = "Delete",
            tone = ConfirmTone.Danger,
            onConfirm = { container.intent(TemplatesListContract.Intent.ConfirmDelete) },
            onDismiss = { container.intent(TemplatesListContract.Intent.CancelDelete) },
        )
    }

    val tpl = testingTemplate
    if (tpl != null && !workspaceId.isNullOrBlank()) {
        TestFireModal(
            workspaceId = workspaceId,
            sectionsConfig = tpl.sectionsConfig,
            teamsStore = standupsStore,
            store = templatesStore,
            onDismiss = { testingTemplate = null },
        )
    }
}

@Composable
private fun TemplatesListView(
    state: TemplatesListContract.State,
    selectedId: String?,
    workspaceName: String?,
    onToggleSelect: (String) -> Unit,
    onTest: (TemplateDto) -> Unit,
    dispatch: (TemplatesListContract.Intent) -> Unit,
) {
    Box(
        modifier = Modifier.fillMaxWidth()
            .styleModifier { property("margin", "0 auto") }
            .maxWidth(Layout.wide),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AppPageHeader(
                title = "Templates",
                eyebrow = "admin",
                subtitle = "Sections and headings apply to the standups that use a template. The prompt text is a preview only for now. Click a card to preview it.",
                action = {
                    Button(
                        label = "New template",
                        variant = ButtonVariant.Primary,
                        onClick = { dispatch(TemplatesListContract.Intent.OpenNew) },
                    )
                },
            )

            StateSwap(
                state = state.phase,
                modifier = Modifier.fillMaxWidth().margin(top = Spacing.md),
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(Spacing.md),
                ) {
                    val isLoading = state.status == TemplatesListContract.Status.Idle ||
                        state.status == TemplatesListContract.Status.Loading
                    if (isLoading && state.items.isEmpty()) {
                        repeat(3) { SkeletonTemplateCard() }
                    } else {
                        state.items.forEach { t ->
                            TemplateCard(
                                template = t,
                                expanded = selectedId == t.id,
                                workspaceName = workspaceName,
                                onToggle = { onToggleSelect(t.id) },
                                onTest = { onTest(t) },
                                dispatch = dispatch,
                            )
                        }
                        if (state.items.isEmpty()) {
                            EmptyOrStatusText(
                                state = state,
                                onCreate = { dispatch(TemplatesListContract.Intent.OpenNew) },
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SkeletonTemplateCard() {
    SkeletonCard(modifier = Modifier.styleModifier { property("gap", "12px") }) {
        Row(
            modifier = Modifier.fillMaxWidth().gap(Spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Column(modifier = Modifier.gap(Spacing.xs).styleModifier { property("min-width", "0") }) {
                SkeletonLine(widthPct = 40, heightPx = 16)
                SkeletonLine(widthPct = 64, heightPx = 11)
            }
            Row(horizontalArrangement = Arrangement.spacedBy(Spacing.xs), verticalAlignment = Alignment.CenterVertically) {
                SkeletonBox(widthPx = 56, heightPx = 30, radiusPx = 8)
                SkeletonBox(widthPx = 56, heightPx = 30, radiusPx = 8)
            }
        }
        Row(modifier = Modifier.gap(Spacing.xs).styleModifier { property("flex-wrap", "wrap") }) {
            SkeletonBox(widthPx = 140, heightPx = 22, radiusPx = 999)
            SkeletonBox(widthPx = 120, heightPx = 22, radiusPx = 999)
            SkeletonBox(widthPx = 110, heightPx = 22, radiusPx = 999)
        }
    }
}

@Composable
private fun TemplateCard(
    template: TemplateDto,
    expanded: Boolean,
    workspaceName: String?,
    onToggle: () -> Unit,
    onTest: () -> Unit,
    dispatch: (TemplatesListContract.Intent) -> Unit,
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("padding", "0")
                property("transition", Motion.TRANSITION_HOVER)
                if (expanded) property("border", "1px solid ${Palette.accentBorder}")
            },
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            HeaderSection(
                template = template,
                expanded = expanded,
                onToggle = onToggle,
                onTest = onTest,
                dispatch = dispatch,
            )
            if (expanded) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("height", "1px")
                            property("background-color", "${Palette.borderSubtle}")
                        },
                ) {}
                Box(modifier = Modifier.fillMaxWidth().padding(Spacing.md)) {
                    TemplatePreviewPane(
                        promptBody = template.promptBody,
                        sectionsConfig = template.sectionsConfig,
                        workspaceName = workspaceName,
                    )
                }
            }
        }
    }
}

@Composable
private fun HeaderSection(
    template: TemplateDto,
    expanded: Boolean,
    onToggle: () -> Unit,
    onTest: () -> Unit,
    dispatch: (TemplatesListContract.Intent) -> Unit,
) {
    val now = Clock.System.now()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(Spacing.md)
            .cursor(Cursor.Pointer)
            .onClick { onToggle() },
        verticalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().gap(Spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Column(
                modifier = Modifier
                    .gap(Spacing.xxs)
                    .styleModifier {
                        property("flex", "1 1 auto")
                        property("min-width", "0")
                    },
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
                ) {
                    SpanText(
                        text = template.name,
                        modifier = Modifier
                            .fontSize(Typography.h3.size)
                            .fontWeight(Typography.h3.weight)
                            .color(Palette.textPrimary),
                    )
                    if (template.isDefault) Badge(label = "Default", tone = BadgeTone.Accent)
                    ExpandChevron(expanded)
                }
                template.description?.takeIf { it.isNotBlank() }?.let { d ->
                    SpanText(
                        text = d,
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textSecondary),
                    )
                }
            }
            Row(
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    label = "Test",
                    variant = ButtonVariant.Secondary,
                    size = ButtonSize.Sm,
                    onClick = { onTest() },
                )
                if (template.isDefault) {
                    Button(
                        label = "Duplicate",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        onClick = { dispatch(TemplatesListContract.Intent.OpenDuplicate) },
                    )
                    Button(
                        label = "View",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        onClick = { dispatch(TemplatesListContract.Intent.OpenDetail(template.id)) },
                    )
                } else {
                    Button(
                        label = "Edit",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        onClick = { dispatch(TemplatesListContract.Intent.OpenDetail(template.id)) },
                    )
                    Button(
                        label = "Delete",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        onClick = { dispatch(TemplatesListContract.Intent.RequestDelete(template.id)) },
                    )
                }
            }
        }
        MetaRow(template = template, now = now)
    }
}

@Composable
private fun MetaRow(template: TemplateDto, now: kotlinx.datetime.Instant) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .gap(Spacing.xs)
            .styleModifier {
                property("flex-wrap", "wrap")
                property("align-items", "center")
            },
    ) {
        SectionsChip(template)
        PromptSizeChip(template.promptBody)
        UpdatedChip(template, now)
    }
}

@Composable
private fun SectionsChip(template: TemplateDto) {
    val enabled = template.sectionsConfig.includedSlots().map { it.label }
    val label = if (enabled.isEmpty()) "No sections" else enabled.joinToString(" · ")
    MetaChip(icon = { LucideList(size = 14) }, text = label)
}

@Composable
private fun PromptSizeChip(prompt: String) {
    val words = if (prompt.isBlank()) 0 else prompt.trim().split(Regex("\\s+")).size
    val chars = prompt.length
    MetaChip(icon = { LucideFileText(size = 14) }, text = "$words words · $chars chars")
}

@Composable
private fun UpdatedChip(template: TemplateDto, now: kotlinx.datetime.Instant) {
    val label = "Updated ${RelativeTime.format(template.updatedAt, now)}"
    MetaChip(icon = { LucideClock(size = 14) }, text = label)
}

@Composable
private fun MetaChip(icon: @Composable () -> Unit, text: String) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.styleModifier {
            property("gap", "6px")
            property("background-color", "${Palette.surfaceSubtle}")
            property("border-radius", "999px")
            property("padding", "3px 10px")
            property("min-width", "0")
        },
    ) {
        Box(
            modifier = Modifier
                .ariaHidden()
                .color(Palette.textTertiary)
                .styleModifier { property("display", "inline-flex") },
        ) {
            icon()
        }
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500)
                .color(Palette.textSecondary),
        )
    }
}

@Composable
private fun ExpandChevron(expanded: Boolean) {
    Box(
        modifier = Modifier
            .ariaHidden()
            .color(Palette.textTertiary)
            .styleModifier {
                property("display", "inline-flex")
                property("transition", "transform ${Motion.DUR_BASE} ${Motion.EASE_STANDARD}")
                property("transform", if (expanded) "rotate(0deg)" else "rotate(-90deg)")
            },
    ) {
        LucideChevronDown(size = 16)
    }
}

@Composable
private fun EmptyOrStatusText(state: TemplatesListContract.State, onCreate: () -> Unit) {
    when (val status = state.status) {
        TemplatesListContract.Status.Ready ->
            EmptyState(
                title = "No templates yet",
                subtitle = "Create a template to choose which sections your standup has and what they're called. The prompt text is a preview only for now.",
                icon = { LucideFileText(size = 26) },
                ctaLabel = "New template",
                onCta = onCreate,
            )
        TemplatesListContract.Status.Idle, TemplatesListContract.Status.Loading ->
            SpanText(
                text = "Loading templates…",
                modifier = Modifier
                    .color(Palette.textSecondary)
                    .fontSize(Typography.body.size),
            )
        is TemplatesListContract.Status.Error ->
            SpanText(
                text = "Could not load templates: ${status.message}",
                modifier = Modifier
                    .color(Palette.error)
                    .fontSize(Typography.body.size),
            )
    }
}
